import { useState } from 'react'
import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import { Column } from '@10xLabs/react-web/components/Column'
import Heading from 'components/v2/Heading'
import { Input } from 'components/Input'
import { Row } from '@10xLabs/react-web/components/Row'
import FormLayout from 'features/App/components/FormLayout'
import { BulkItemsForm } from 'features/Inventories/components/BulkItemsForm'
import { BulkItemsSummary } from 'features/Inventories/components/BulkItemsSummary'
import { InventoryAutocomplete, getInventoryLabel } from 'features/Inventories/components/InventoryAutocomplete'
import { ORDER_TYPE } from 'features/Inventories/components/OrderType/constants'
import { InputSupplierSuggestion } from 'features/Inventories/components/InputSupplierSuggestion'
import { sanitizePrice } from 'utils/math'
import { schema } from './schema'

const messages = defineMessages({
  purchaseIdPlaceholder: {
    id: 'spanish.items.purchaseId',
    defaultMessage: 'Compra id'
  },
  supplierPlaceholder: {
    id: 'spanish.items.supplier',
    defaultMessage: 'Proveedor'
  },
  destinationPlaceholder: {
    id: 'spanish.inventories.items.destinationPlaceholder',
    defaultMessage: 'Seleccionar destino'
  }
})

const EditOrderForm = (props: TSFixMe): TSFixMe => {
  const {
    companyId,
    values,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    errors,
    touched,
    cancelLink,
    order
  } = props

  const intl = useIntl()

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormLayout.CardContent>
        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="spanish.orders.edit.form.general.title" defaultMessage="General" />
            </Heading.H5>
          </Column>
        </Row>

        {values.type === ORDER_TYPE.PURCHASE && (
          <>
            <Row>
              <Column width="50%">
                <Input
                  id="purchaseId"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.purchaseId}
                  placeholder={intl.formatMessage(messages.purchaseIdPlaceholder)}
                  touched={touched.purchaseId}
                  errors={errors.purchaseId}
                  label={<FormattedMessage id="spanish.items.purchaseId" defaultMessage="Compra id" />}
                />
              </Column>
            </Row>

            <Row>
              <Column width="50%">
                <InputSupplierSuggestion
                  id="supplier"
                  onChange={(supplier: TSFixMe): TSFixMe => setFieldValue('supplier', supplier)}
                  onBlur={handleBlur}
                  value={values.supplier}
                  companyId={companyId}
                  placeholder={intl.formatMessage(messages.supplierPlaceholder)}
                  label={<FormattedMessage id="spanish.dictionary.supplier" defaultMessage="Proveedor" />}
                  touched={touched.supplier}
                  errors={errors.supplier}
                />
              </Column>
            </Row>
          </>
        )}

        {values.type === ORDER_TYPE.SHIPMENT && (
          <Row>
            <Column width="50%">
              <InventoryAutocomplete
                placeholder={intl.formatMessage(messages.destinationPlaceholder)}
                value={getInventoryLabel(values.destination, intl)}
                label={<FormattedMessage id="spanish.dictionary.destination" defaultMessage="Destino" />}
                onChange={(inventory: TSFixMe): TSFixMe => {
                  setFieldValue('destination', inventory)
                }}
                excludedInventories={[order.origin.id]}
                companyId={companyId}
                touched={touched.destination && touched.destination.id}
                errors={errors.destination && errors.destination.id}
              />
            </Column>
          </Row>
        )}

        <Row>
          <Column>
            <Heading.H5>
              <FormattedMessage id="spanish.orders.edit.form.items.title" defaultMessage="Productos" />
            </Heading.H5>
          </Column>
        </Row>

        <BulkItemsForm {...props} noPrice={values.type === ORDER_TYPE.SHIPMENT} />
      </FormLayout.CardContent>

      <FormLayout.Split />

      <FormLayout.CardFooter>
        <FormLayout.CardFooterActions>
          <Button as={NavLink} to={cancelLink} kind="cancel">
            <FormattedMessage id="spanish.dictionary.cancel" defaultMessage="Cancelar" />
          </Button>

          <Button type="submit" kind="primary">
            <FormattedMessage id="spanish.orders.edit.form.next" defaultMessage="Ver resumen" />
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

export const EditOrder = ({ order = {}, cancelLink, onSubmit, error, loading }: TSFixMe): TSFixMe => {
  const intl = useIntl()

  const initialValues = {
    type: order.type,
    status: order.status,
    purchaseId: order.purchaseID || '',
    supplier: order.supplier || '',
    destination: order.destination || { id: '', name: '' },
    items: order.lineItems.map((item: TSFixMe): TSFixMe => {
      return {
        isPackage: false,
        isMixed: false,
        packagesQuantity: '',
        nestedItems: [
          {
            id: item.itemID,
            name: item.name,
            units: item.units,
            netContent: item.netContent,
            container: item.container,
            quantity: item.ordered,
            price: sanitizePrice(item.cost / 100)
          }
        ]
      }
    })
  }

  const [step, setStep] = useState(1)

  const handleSubmit = (values: TSFixMe): TSFixMe => {
    if (step === 1) {
      setStep(2)
    } else {
      onSubmit(values)
    }
  }

  return (
    <Formik initialValues={initialValues} validationSchema={schema(intl)} onSubmit={handleSubmit}>
      {(props): TSFixMe => {
        switch (step) {
          case 1:
            return (
              <EditOrderForm
                {...props}
                order={order}
                companyId={order.company && order.company.id}
                cancelLink={cancelLink}
              />
            )
          case 2:
            return <BulkItemsSummary {...props} error={error} loading={loading} onCancel={(): TSFixMe => setStep(1)} />
        }
      }}
    </Formik>
  )
}
